import React, { useEffect, useState, useCallback } from "react";
import {
  Phone, PhoneCall, ShieldCheck, ShieldAlert, Loader2, ChevronDown, ChevronUp,
  Play, Languages, RefreshCw, Sparkles, ExternalLink,
} from "lucide-react";
import { api } from "../../api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

export function ScoreBadge({ score }) {
  if (score === null || score === undefined) return <span className="text-[11px] text-slate-400">—</span>;
  const color = score >= 70 ? "#16a34a" : score >= 40 ? "#d97706" : "#dc2626";
  const bg = score >= 70 ? "#dcfce7" : score >= 40 ? "#fef3c7" : "#fee2e2";
  return (
    <span data-testid="ai-call-score-badge" className="text-[11px] font-semibold px-2 py-0.5 rounded"
      style={{ color, background: bg }}>
      {score}/100
    </span>
  );
}

export function StatusPill({ status }) {
  const map = {
    queued: { label: "Queued", bg: "#e2e8f0", fg: "#475569" },
    failed: { label: "Failed", bg: "#fee2e2", fg: "#dc2626" },
    scored: { label: "Scored", bg: "#e0f2fe", fg: "#0369a1" },
    completed: { label: "Completed", bg: "#dcfce7", fg: "#16a34a" },
    answered: { label: "Answered", bg: "#dcfce7", fg: "#16a34a" },
  };
  const m = map[status] || { label: status || "—", bg: "#f1f5f9", fg: "#64748b" };
  return <span className="text-[10.5px] font-medium px-2 py-0.5 rounded uppercase tracking-wide" style={{ background: m.bg, color: m.fg }}>{m.label}</span>;
}

export function CallRow({ call }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button data-testid={`ai-call-row-${call.id}`} onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left">
        <div className="w-8 h-8 rounded-full bg-emerald-50 grid place-items-center shrink-0">
          <PhoneCall size={14} className="text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium text-slate-800 truncate">{call.lead_name || "Unknown lead"} · {call.phone}</div>
          <div className="text-[11px] text-slate-500">{new Date(call.created).toLocaleString()} · {(call.language || "auto").toUpperCase()}</div>
        </div>
        {call.is_demo && (
          <span className="text-[9.5px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide bg-slate-200 text-slate-500" title="Simulated call — excluded from analytics">
            Demo
          </span>
        )}
        <StatusPill status={call.status} />
        <ScoreBadge score={call.ai_score} />
        {call.verified === true && <ShieldCheck size={15} className="text-emerald-500" />}
        {call.verified === false && <ShieldAlert size={15} className="text-amber-500" />}
        {open ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 bg-slate-50 space-y-2">
          {call.script && <div className="text-[12px] text-slate-600"><b>Opening script:</b> {call.script}</div>}
          {call.transcript && <div className="text-[12px] text-slate-600 whitespace-pre-line"><b>Transcript:</b> {call.transcript}</div>}
          {call.interest_level && <div className="text-[12px] text-slate-600"><b>Interest:</b> {call.interest_level}</div>}
          {call.follow_up_recommendation && <div className="text-[12px] text-slate-600"><b>Follow-up:</b> {call.follow_up_recommendation}</div>}
          {call.reason && <div className="text-[12px] text-rose-500"><b>Error:</b> {call.reason}</div>}
          {call.recording_url && (
            <audio data-testid="ai-call-recording-player" controls
              src={`${call.recording_url}${call.recording_url.includes("?") ? "&" : "?"}token=${encodeURIComponent(localStorage.getItem("aura_token") || "")}`}
              className="w-full h-8 mt-1" />
          )}
        </div>
      )}
    </div>
  );
}

export const AIVoiceAgentView = () => {
  const [calls, setCalls] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  const load = useCallback(async (includeDemo) => {
    setLoading(true);
    try {
      const [c, a, s] = await Promise.all([
        api.get("/crm/ai-calls", { params: { include_demo: includeDemo } }),
        api.get("/crm/ai-calls/analytics"),
        api.get("/crm/ai-voice-settings"),
      ]);
      setCalls(c.data || []);
      setAnalytics(a.data || null);
      setSettings(s.data || null);
    } catch (e) { console.warn("[AIVoiceAgentView] load failed", e?.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(showDemo); }, [load, showDemo]);

  const toggleAutoCall = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { data } = await api.put("/crm/ai-voice-settings", {
        auto_call_enabled: !settings.auto_call_enabled,
        languages: settings.languages,
      });
      setSettings(data);
    } catch (e) { console.warn(e); }
    setSaving(false);
  };

  if (loading && !analytics) {
    return <div className="h-full grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div data-testid="ai-voice-agent-view" className="p-5 md:p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Phone size={17} className="text-emerald-600" /> AI Voice Agent
          </h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            Auto-verifies Google/Meta ad leads by phone &amp; scores intent — powered by {settings?.engine === "elevenlabs" ? "ElevenLabs" : "OpenAI TTS/Whisper"}.
          </p>
        </div>
        <button data-testid="ai-voice-refresh" onClick={load}
          className="text-[11px] px-3 py-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1.5">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <button
        data-testid="ai-voice-open-full-dashboard"
        onClick={() => window.dispatchEvent(new CustomEvent("hexa:open-app", { detail: { id: "voiceagent" } }))}
        className="text-[11.5px] px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 flex items-center gap-1.5 hover:bg-emerald-100 transition w-fit"
      >
        <ExternalLink size={12} /> Open full AI Voice Agent dashboard (minutes, demo call, CRM link toggle)
      </button>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total AI Calls", value: analytics?.total ?? 0 },
          { label: "Connect Rate", value: `${analytics?.connect_rate ?? 0}%` },
          { label: "Verified Rate", value: `${analytics?.verified_rate ?? 0}%` },
          { label: "Avg Score", value: `${analytics?.avg_score ?? 0}/100` },
        ].map((k) => (
          <div key={k.label} data-testid={`ai-voice-kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}
            className="rounded-lg border border-slate-200 p-3.5 bg-white">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-medium">{k.label}</div>
            <div className="text-xl font-semibold text-slate-800 mt-1">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Trend + Settings */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 rounded-lg border border-slate-200 p-4 bg-white">
          <div className="text-[12px] font-semibold text-slate-700 mb-2">Calls — last 7 days</div>
          <div style={{ width: "100%", height: 160 }}>
            <ResponsiveContainer>
              <BarChart data={analytics?.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#16a34a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 p-4 bg-white space-y-3">
          <div className="text-[12px] font-semibold text-slate-700">Settings</div>
          <div className="flex items-center justify-between">
            <span className="text-[12px] text-slate-600">Auto-call new ad leads</span>
            <button data-testid="ai-voice-auto-call-toggle" onClick={toggleAutoCall} disabled={saving}
              role="switch" aria-checked={!!settings?.auto_call_enabled}
              className={`w-10 h-5.5 rounded-full relative transition ${settings?.auto_call_enabled ? "bg-emerald-500" : "bg-slate-300"}`}
              style={{ height: 22, width: 40 }}>
              <span className="absolute top-0.5 left-0.5 w-4.5 h-4.5 rounded-full bg-white transition"
                style={{ transform: settings?.auto_call_enabled ? "translateX(18px)" : "translateX(0)", width: 18, height: 18 }} />
            </button>
          </div>
          <div className="flex items-center justify-between text-[12px]">
            <span className="text-slate-600">Voice engine</span>
            <span className={`font-medium ${settings?.elevenlabs_configured ? "text-emerald-600" : "text-amber-600"}`}>
              {settings?.elevenlabs_configured ? "ElevenLabs" : "OpenAI (fallback)"}
            </span>
          </div>
          <div className="flex items-center justify-between text-[12px]">
            <span className="text-slate-600">Telephony (DialNexa)</span>
            <span
              data-testid="ai-voice-dialnexa-status"
              className={`font-medium ${
                settings?.dialnexa_status === "valid" ? "text-emerald-600"
                  : settings?.dialnexa_status === "invalid" ? "text-rose-500"
                  : settings?.dialnexa_configured ? "text-amber-600" : "text-rose-500"
              }`}
            >
              {settings?.dialnexa_status === "valid" ? "Connected"
                : settings?.dialnexa_status === "invalid" ? "Invalid key"
                : settings?.dialnexa_configured ? "Not verified" : "Not connected"}
            </span>
          </div>
          <div className="pt-1 border-t border-slate-100">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-medium mb-1.5 flex items-center gap-1">
              <Languages size={11} /> Languages
            </div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(settings?.language_labels || {}).map(([code, label]) => (
                <span key={code} data-testid={`ai-voice-lang-${code}`}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{label}</span>
              ))}
            </div>
          </div>
          {!settings?.dialnexa_configured && (
            <div className="text-[10.5px] text-amber-600 flex items-start gap-1 pt-1">
              <Sparkles size={11} className="mt-0.5 shrink-0" /> Add a DialNexa key in Admin AI → Integrations to enable real outbound calls.
            </div>
          )}
        </div>
      </div>

      {/* Call logs */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[12px] font-semibold text-slate-700">Call logs</div>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="ai-calls-show-demo"
              checked={showDemo}
              onChange={(e) => setShowDemo(e.target.checked)}
            />
            Show demo calls
          </label>
        </div>
        {calls.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-[13px] border border-dashed border-slate-200 rounded-lg">
            No AI calls yet — they'll appear here automatically when a Google/Meta ad lead comes in.
          </div>
        ) : (
          <div className="space-y-2">
            {calls.map((c) => <CallRow key={c.id} call={c} />)}
          </div>
        )}
      </div>
    </div>
  );
};
